import { useEffect, useState, type JSX, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { MeResponse } from '@netdeck/contracts';
import { useTranslation } from '../i18n';
import { LanguageSwitch } from './LanguageSwitch';
import { useHealth } from '../../features/health/useHealth';
import { OrganizationDialog } from '../../features/organization/OrganizationDialog';
import { BrandMark } from './BrandMark';
import { Icon } from './Icon';
import { Signature } from './Signature';
import { isVisible, NAV_GROUPS, type NavItem } from './navigation';
import { readNavCollapsed, writeNavCollapsed } from './shellPreferences';
import { StatusDot, type Status } from './StatusDot';
import { useTheme } from './theme';
import { useViewport } from './viewport';

/**
 * Размер глифа в свёрнутом меню.
 *
 * Единственное место, где набор отходит от общих 16 px (см. `assets/icons.md`),
 * и отходит осознанно: в полосе иконка остаётся от пункта одна — ни подписи,
 * ни счётчика рядом нет, — и в 16 px по ней приходится целиться. 24 px дают
 * и попадание пальцем, и различимую форму, а разнобоя не создают: развёрнутое
 * меню, где иконка стоит рядом с текстом, остаётся на 16 px.
 */
const COLLAPSED_ICON_SIZE = 24;

export interface AppShellProps {
  me: MeResponse;
  activeId: string;
  onNavigate: (id: string) => void;
  onLogout: () => void;
  /**
   * Счётчики у пунктов меню: сколько чего открыто прямо сейчас.
   *
   * Нужны разделам, которые продолжают работать, когда на них не смотрят.
   * VNC-консоль именно такая: сессии живут, пока их не закрыли, и человек
   * должен видеть со стороны, что три машины у него всё ещё под рукой.
   */
  badges?: Readonly<Record<string, number>>;
  children: ReactNode;
}

export function AppShell({
  me,
  activeId,
  onNavigate,
  onLogout,
  badges,
  children,
}: AppShellProps): JSX.Element {
  const { t } = useTranslation();

  const granted = new Set<string>(me.permissions);

  /*
    Группы без единого доступного пункта отбрасываются здесь, а не при
    отрисовке: свёрнутому меню нужен номер группы среди показанных, чтобы
    не рисовать разделитель над самой первой.
  */
  const groups = NAV_GROUPS.map((group) => ({
    titleKey: group.titleKey,
    items: group.items.filter((item) => isVisible(item, granted)),
  })).filter((group) => group.items.length > 0);

  /**
   * Меню колонкой или ящиком.
   *
   * Колонка в 200 px — это треть планшета и половина телефона, а полоса
   * иконок в 52 px хоть и дешевле, но отнимает место постоянно, ради
   * навигации, которой пользуются раз в несколько минут. Поэтому у́же
   * десктопа меню становится ящиком: его нет, пока не позвали.
   */
  const viewport = useViewport();
  const compact = viewport !== 'desktop';

  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  /**
   * Открыто ли переименование организации.
   *
   * Правит его администратор организации — тем же правом, которым заводит
   * пользователей. Остальным название показывается текстом: видеть, чьи
   * это площадки, должен каждый, менять — не каждый.
   */
  const [orgOpen, setOrgOpen] = useState(false);
  const mayRenameOrg = granted.has('org:admin');

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  /*
    Окно расширили до десктопа — меню снова колонка, и открытый ящик
    остался бы висеть поверх неё чёрной подложкой.
  */
  useEffect(() => {
    if (!compact) {
      setDrawerOpen(false);
    }
  }, [compact]);

  /** Уход в раздел закрывает ящик: держать его открытым поверх нового экрана незачем. */
  const navigate = (id: string): void => {
    onNavigate(id);
    setDrawerOpen(false);
  };

  const sections = (collapsed: boolean): JSX.Element[] =>
    groups.map((group, index) => (
      <div key={group.titleKey} className="contents">
        {/*
          В полосе иконок заголовку группы места нет: обрезанное
          «Админ…» ничего не объясняет. Границы групп при этом видны
          всё равно — их держит разделитель, и только между группами:
          черта над первой отделяла бы её от пустоты.
        */}
        {collapsed ? (
          index > 0 ? (
            <div className="mx-1 my-1.5 border-t border-line-subtle" />
          ) : null
        ) : (
          <div className="flex-none px-2 pt-3.5 pb-1.5 font-mono text-2xs tracking-[0.08em] text-faint uppercase">
            {t(group.titleKey)}
          </div>
        )}
        {group.items.map((item) => (
          <NavButton
            key={item.id}
            item={badges?.[item.id] ? { ...item, badge: badges[item.id] } : item}
            active={item.id === activeId}
            collapsed={collapsed}
            touch={compact}
            onSelect={() => navigate(item.id)}
            t={t}
          />
        ))}
      </div>
    ));

  return (
    <div className="flex h-[100dvh] flex-col bg-surface">
      <header className="flex h-11 flex-none items-center gap-3.5 border-b border-line bg-raised px-3">
        {compact ? (
          <button
            type="button"
            onClick={() => setDrawerOpen((open) => !open)}
            aria-expanded={drawerOpen}
            aria-label={t('nav.sections')}
            className="-ml-1 flex size-9 flex-none items-center justify-center rounded-sm text-muted hover:text-fg"
          >
            <Icon name="menu" size={COLLAPSED_ICON_SIZE} />
          </button>
        ) : null}

        <div className="flex items-center gap-2">
          <BrandMark />
          <span className="text-base font-semibold tracking-[0.02em]">NetDeck</span>
        </div>

        {/*
          Организация, имя и роли на узком экране уезжают в ящик: они
          отвечают на вопрос «кто я и где», который задают раз в сеанс,
          а места в строке занимают больше всего остального вместе.
        */}
        {compact ? null : (
          <>
            <div className="h-5 w-px bg-line" />

            {/*
              Название правится там же, где видно, — иначе искать его пришлось
              бы в разделе, которого нет: организация одна, и своего экрана
              у неё не заведено.
            */}
            <div className="flex h-6.5 items-center gap-1.5 rounded-sm border border-line-strong px-2.5 text-sm">
              <span className="text-muted">{t('organization.label')}</span>
              <span className="font-medium">{me.organization.name}</span>
              {mayRenameOrg ? (
                <button
                  type="button"
                  onClick={() => setOrgOpen(true)}
                  aria-label={t('organization.rename')}
                  title={t('organization.rename')}
                  className="-mr-1 flex items-center text-dim hover:text-fg"
                >
                  <Icon name="pencil" />
                </button>
              ) : null}
            </div>
          </>
        )}

        <div className="ml-auto flex items-center gap-3 font-mono text-xs text-dim">
          <PlatformHealth />
          {compact ? null : (
            <>
              <span className="text-faint">·</span>
              <span title={me.user.email}>{me.user.displayName}</span>
              <span className="text-faint">·</span>
              <span>{me.roles.map((role) => role.name).join(', ') || t('nav.noRoles')}</span>
              <LanguageSwitch />
              <ThemeSwitch />
              <button
                type="button"
                onClick={onLogout}
                className="flex items-center gap-1.5 rounded-sm border border-line px-2 py-1 text-dim hover:text-fg"
              >
                <Icon name="logout" />
                {t('common.action.logout')}
              </button>
            </>
          )}
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {compact ? null : (
          <nav
            aria-label={t('nav.sections')}
            className={`flex flex-none flex-col gap-0.5 overflow-y-auto border-r border-line bg-surface p-2 ${
              navCollapsed ? 'w-13' : 'w-[200px]'
            }`}
          >
            {sections(navCollapsed)}

            <div className="mt-auto flex flex-col gap-1.5 border-t border-line-subtle pt-2.5">
              <button
                type="button"
                onClick={() => {
                  setNavCollapsed(!navCollapsed);
                  writeNavCollapsed(!navCollapsed);
                }}
                aria-expanded={!navCollapsed}
                aria-label={navCollapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
                title={navCollapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
                className={`flex items-center gap-2 rounded-sm text-base text-dim hover:text-fg ${
                  navCollapsed ? 'h-9 justify-center' : 'h-7 px-2'
                }`}
              >
                <Icon
                  name={navCollapsed ? 'chevronRight' : 'chevronLeft'}
                  size={navCollapsed ? COLLAPSED_ICON_SIZE : undefined}
                />
                {navCollapsed ? null : <span>{t('nav.collapse')}</span>}
              </button>

              {/* Подпись фазы в полосе не помещается, а обрезанная только врёт. */}
              {navCollapsed ? null : <Signature className="px-2 pb-0.5" />}
            </div>
          </nav>
        )}

        {compact && drawerOpen ? (
          <div className="fixed inset-0 z-50 flex">
            {/*
              Ящик рисуется `fixed`, а поля безопасной зоны заданы на `body`
              и на него не распространяются: вырез и полоса жеста считаются
              здесь заново, иначе первый пункт уезжает под чёлку.
            */}
            <nav
              aria-label={t('nav.sections')}
              className="flex w-[280px] max-w-[85%] flex-col gap-0.5 overflow-y-auto border-r border-line bg-surface p-2 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] pl-[max(0.5rem,env(safe-area-inset-left))]"
            >
              {sections(false)}

              <div className="mt-auto flex flex-col gap-2 border-t border-line-subtle pt-2.5">
                <dl className="grid grid-cols-[76px_1fr] gap-x-2 gap-y-1 px-2 font-mono text-2xs">
                  <dt className="text-faint">{t('organization.label')}</dt>
                  <dd className="truncate text-dim">
                    {mayRenameOrg ? (
                      <button
                        type="button"
                        onClick={() => {
                          setDrawerOpen(false);
                          setOrgOpen(true);
                        }}
                        aria-label={t('organization.rename')}
                        className="flex max-w-full items-center gap-1.5 text-left"
                      >
                        <span className="truncate">{me.organization.name}</span>
                        <Icon name="pencil" />
                      </button>
                    ) : (
                      me.organization.name
                    )}
                  </dd>
                  <dt className="text-faint">{t('nav.you')}</dt>
                  <dd className="truncate text-dim" title={me.user.email}>
                    {me.user.displayName}
                  </dd>
                  <dt className="text-faint">{t('nav.roles')}</dt>
                  <dd className="truncate text-dim">
                    {me.roles.map((role) => role.name).join(', ') || t('nav.noRoles')}
                  </dd>
                </dl>

                <div className="flex items-center gap-2 px-1">
                  <LanguageSwitch />
                  <ThemeSwitch />
                  <button
                    type="button"
                    onClick={onLogout}
                    className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-sm border border-line text-base text-dim hover:text-fg"
                  >
                    <Icon name="logout" />
                    {t('common.action.logout')}
                  </button>
                </div>

                <Signature className="px-2 pb-0.5" />
              </div>
            </nav>

            {/* Подложка закрывает ящик и заодно не даёт нажать то, что под ним. */}
            <button
              type="button"
              aria-label={t('nav.closeMenu')}
              onClick={() => setDrawerOpen(false)}
              className="flex-1 bg-black/60"
            />
          </div>
        ) : null}

        <main className="flex min-w-0 flex-1 flex-col overflow-auto">{children}</main>
      </div>

      {orgOpen ? (
        <OrganizationDialog name={me.organization.name} onClose={() => setOrgOpen(false)} />
      ) : null}
    </div>
  );
}

/**
 * Переключатель темы в шапке.
 *
 * Место выбрано так, чтобы кнопка не зависела от левого меню: свёрнутое
 * в полосу, оно оставило бы от неё один глиф без объяснений, а тема — то,
 * что ищут глазами один раз и потом не трогают.
 *
 * На кнопке — та тема, в которую она переключит, а не та, что включена
 * сейчас: солнце значит «сделать светло». Иначе нажатие пришлось бы
 * проверять на себе.
 */
function ThemeSwitch(): JSX.Element {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme();

  const label = theme === 'dark' ? t('common.theme.light') : t('common.theme.dark');

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className="flex items-center rounded-sm border border-line px-2 py-1 text-dim hover:text-fg"
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
    </button>
  );
}

/**
 * Состояние платформы в шапке — там же, где в макете стоит индикатор агента.
 *
 * Агенты появятся в фазе 2; до тех пор это место занимает состояние самой
 * платформы. Смысл индикатора одинаков: инженер видит, работает ли система,
 * не уходя со своего экрана.
 */
function PlatformHealth(): JSX.Element {
  const { t } = useTranslation();
  const health = useHealth();

  const down = health.error !== null || health.data?.status === 'degraded';
  const failing = Object.entries(health.data?.dependencies ?? {})
    .filter(([, dependency]) => dependency.status === 'down')
    .map(([name]) => name);

  /*
    Медиасервер не роняет платформу, но и молчать о нём нельзя: без go2rtc
    не работают видеостена и архив, и человек должен узнать об этом здесь,
    а не по чёрному экрану ячейки. Отсюда третье состояние — «частично».
  */
  const partial = !down && failing.length > 0;

  const status: Status = health.isPending ? 'unknown' : down ? 'down' : partial ? 'warn' : 'up';

  const label = down
    ? t('health.platform.degraded')
    : partial
      ? t('health.platform.partial', { list: failing.join(', ') })
      : t('health.platform.healthy');

  return (
    <span
      className="flex items-center gap-1.5"
      title={
        health.error
          ? t('health.platform.noApi')
          : failing.length > 0
            ? t('health.platform.unavailable', { list: failing.join(', ') })
            : t('health.platform.allUp')
      }
    >
      <StatusDot status={status} size={7} label={label} />
      <span className={down ? 'text-down-fg' : partial ? 'text-warn' : undefined}>
        {health.isPending
          ? t('health.platform.checking')
          : down
            ? t('health.platform.failed')
            : partial
              ? t('health.platform.partialShort')
              : t('health.platform.title')}
      </span>
    </span>
  );
}

/**
 * Пункт меню.
 *
 * Свёрнутый пункт — та же кнопка без подписи: название уходит в подсказку
 * при наведении и в `aria-label`, поэтому раздел остаётся доступен и с мыши,
 * и с клавиатуры, и скринридеру. Иконка при этом обязательна — без неё
 * от пункта в полосе не осталось бы ничего.
 */
function NavButton({
  item,
  active,
  collapsed,
  touch,
  onSelect,
  t,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  /** Пункт в ящике: по нему целятся пальцем, а не курсором. */
  touch: boolean;
  onSelect: () => void;
  /*
    Переводчик передаётся сверху, а не берётся своим хуком: пунктов
    полтора десятка, и пятнадцать подписок на смену языка ради одной
    и той же функции — пятнадцать лишних перерисовок на каждое
    переключение.
  */
  t: TFunction;
}): JSX.Element {
  const label = t(item.labelKey);
  const hint = item.planned ? t('nav.plannedHint', { label }) : label;

  return (
    <button
      type="button"
      disabled={item.planned}
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? hint : undefined}
      title={collapsed || item.planned ? hint : undefined}
      className={[
        /*
          `flex-none` обязателен: пунктов пятнадцать, ящик у них флексовая
          колонка с прокруткой, и без запрета на сжатие флекс ужимает
          заданную высоту, чтобы уместить всё разом. Кнопка в 44 px
          превращалась в 24, то есть ровно в то, от чего уходили.
        */
        'relative flex flex-none items-center rounded-sm text-left text-base',
        /*
          В полосе кнопка выше: глиф в 24 px в прежние 28 px упирался бы
          в края, и ряд пунктов читался бы сплошной лентой без промежутков.
        */
        collapsed
          ? 'h-9 justify-center'
          : // 44 px под палец — столько отводят пальцу и Apple, и Google;
            // 28 px десктопной строки пальцем читаются как «промахнулся».
            `justify-between gap-2 px-2 ${touch ? 'h-11' : 'h-7'}`,
        active ? 'bg-overlay font-medium shadow-[inset_2px_0_0_var(--nd-text)]' : 'text-muted',
        item.planned ? 'cursor-not-allowed text-faint' : 'hover:text-fg',
      ].join(' ')}
    >
      <span className={collapsed ? undefined : 'flex min-w-0 items-center gap-2'}>
        <Icon name={item.icon} size={collapsed ? COLLAPSED_ICON_SIZE : undefined} />
        {collapsed ? null : <span className="truncate">{label}</span>}
      </span>

      {collapsed ? (
        /*
          Счётчик в полосе — не число, а точка: две цифры рядом с иконкой
          нечитаемы даже в 24 px, а знать надо одно — там что-то есть. Точное
          число покажет развёрнутое меню. Точка прижата к самому углу: глиф
          подрос, и от прежнего отступа она легла бы ему на плечо.
        */
        item.badge ? (
          <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-down" />
        ) : null
      ) : (
        <>
          {item.planned ? (
            <span className="font-mono text-2xs text-faint">{t('nav.soon')}</span>
          ) : null}
          {item.badge ? <span className="font-mono text-xs text-down">{item.badge}</span> : null}
        </>
      )}
    </button>
  );
}
